import { Link, useParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import { TrendingUp, Building, Landmark, FileSearch, BarChart3, Briefcase, ArrowRight, CheckCircle2 } from 'lucide-react';
import SEO, { generateServiceSchema } from '@/components/SEO';

const fadeInUp = {
  hidden: { opacity: 0, y: 30 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.6 } }
};

const staggerContainer = {
  hidden: { opacity: 0 },
  visible: { opacity: 1, transition: { staggerChildren: 0.1 } }
};

const CapitalMarketsPage = () => {
  const { locale } = useParams();

  const services = [
    { 
      icon: Building, 
      title: 'Investment Sales', 
      description: 'End-to-end transaction advisory for commercial real estate acquisitions and dispositions across all asset classes.',
      items: ['Single asset & portfolio sales', 'Sale-leaseback transactions', 'Institutional investor advisory', 'Cross-border transactions', 'Off-market deal sourcing']
    },
    { 
      icon: Landmark, 
      title: 'Debt & Structured Finance', 
      description: 'Comprehensive debt placement and financing solutions for real estate investors and developers.',
      items: ['Acquisition financing', 'Construction & development loans', 'Refinancing & recapitalization', 'Mezzanine & bridge financing', 'CMBS & agency lending']
    },
    { 
      icon: TrendingUp, 
      title: 'Equity Advisory', 
      description: 'Strategic equity capital raising and joint venture structuring for real estate investments.',
      items: ['Equity placement', 'Joint venture structuring', 'Fund formation advisory', 'LP/GP negotiations', 'Recapitalization strategies']
    },
    { 
      icon: BarChart3, 
      title: 'Valuation & Advisory', 
      description: 'Independent valuation services and strategic advisory for informed investment decisions.',
      items: ['Market value appraisals', 'Portfolio valuations', 'Feasibility studies', 'Highest & best use analysis', 'Due diligence support']
    },
    { 
      icon: FileSearch, 
      title: 'Research & Market Intelligence', 
      description: 'Data-driven insights and market analysis to support capital allocation decisions.',
      items: ['Market forecasting', 'Investment strategy research', 'Competitive benchmarking', 'Submarket analysis', 'Capital flow tracking']
    },
    { 
      icon: Briefcase, 
      title: 'Corporate Capital Solutions', 
      description: 'Tailored financing solutions for corporate real estate requirements and portfolio optimization.',
      items: ['Corporate sale-leasebacks', 'Build-to-suit financing', 'Portfolio restructuring', 'Occupier-investor alignment', 'Net lease advisory']
    },
  ];

  const assetClasses = [
    'Office',
    'Industrial & Logistics',
    'Retail',
    'Multifamily / Residential',
    'Hospitality',
    'Healthcare',
    'Data Centers',
    'Mixed-Use'
  ];

  const whyChooseUs = [
    { title: 'Market Intelligence', desc: 'Deep local market knowledge combined with global capital flow insights' },
    { title: 'Investor Network', desc: 'Established relationships with institutional investors, family offices, and sovereign funds' },
    { title: 'Execution Excellence', desc: 'Proven track record of closing complex transactions across market cycles' },
    { title: 'Full-Service Platform', desc: 'Integrated approach combining investment sales, debt, equity, and advisory services' }
  ];

  const stats = [
    { value: '₹5,000+ Cr', label: 'Transaction Volume Advised' },
    { value: '50+', label: 'Transactions Closed' },
    { value: '2', label: 'Countries Coverage' },
    { value: '100%', label: 'Client Retention Rate' }
  ];

  const serviceSchema = generateServiceSchema({
    type: 'Real Estate Capital Markets',
    name: 'Capital Markets',
    description: 'Full-service real estate capital markets advisory including investment sales, debt & structured finance, equity advisory, and valuation services.'
  });

  return (
    <div className="pt-20" data-testid="capital-markets-page">
      <SEO 
        title="Capital Markets"
        description="Full-service real estate capital markets advisory including investment sales, debt & structured finance, equity advisory, valuation services across all commercial asset classes."
        path="/services/capital-markets"
        structuredData={serviceSchema}
      />

      {/* Hero */}
      <section className="relative py-20 md:py-28 bg-gradient-to-br from-white via-light-grey to-white overflow-hidden">
        <div className="absolute top-0 right-0 w-1/3 h-full bg-teal/5" />
        
        <div className="max-w-7xl mx-auto px-6 relative z-10">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <motion.div initial={{ opacity: 0, x: -50 }} animate={{ opacity: 1, x: 0 }} transition={{ duration: 0.8 }}>
              <span className="inline-flex items-center px-4 py-2 bg-teal/10 text-teal rounded-full text-sm font-medium mb-6">
                <TrendingUp className="w-4 h-4 mr-2" />
                Real Estate Investment
              </span>
              <h1 className="font-playfair text-4xl md:text-5xl lg:text-6xl text-navy leading-tight mb-6">
                Capital Markets
              </h1>
              <p className="text-lg text-mid-grey leading-relaxed mb-8">
                Full-service real estate capital markets advisory delivering investment sales, debt & structured finance, equity placement, and valuation services. We connect investors with opportunities and help owners maximize asset value across all commercial real estate sectors.
              </p>
              <Link to={`/${locale}/contact`} className="btn-primary">
                Discuss Your Investment
              </Link>
            </motion.div>
            
            <motion.div initial={{ opacity: 0, x: 50 }} animate={{ opacity: 1, x: 0 }} transition={{ duration: 0.8, delay: 0.2 }} className="hidden lg:block">
              <img 
                src="/images/external/photo-1486406146926-c627a92ad1ab.jpg"
                alt="Commercial real estate skyline"
                className="rounded-2xl shadow-2xl w-full h-[400px] object-cover"
              />
            </motion.div>
          </div>
        </div>
      </section>

      {/* Stats */}
      <section className="py-12 bg-navy">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
            {stats.map((stat, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="text-center"
              >
                <div className="text-2xl md:text-3xl font-bold text-white mb-1">{stat.value}</div>
                <p className="text-white/70 text-sm">{stat.label}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Services */}
      <section className="py-20 md:py-28 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={staggerContainer} className="text-center mb-16">
            <motion.span variants={fadeInUp} className="text-teal font-medium text-sm uppercase tracking-wider">Our Services</motion.span>
            <motion.h2 variants={fadeInUp} className="font-playfair text-3xl md:text-4xl text-navy mt-4">Capital Markets Solutions</motion.h2>
            <motion.p variants={fadeInUp} className="text-mid-grey mt-4 max-w-2xl mx-auto">
              Comprehensive capital markets services designed to optimize investment outcomes across the entire transaction lifecycle.
            </motion.p>
          </motion.div>

          <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={staggerContainer} className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {services.map((service) => {
              const IconComponent = service.icon;
              return (
                <motion.div key={service.title} variants={fadeInUp} className="bg-light-grey p-6 rounded-xl hover:shadow-lg transition-shadow">
                  <div className="w-12 h-12 bg-teal/10 rounded-xl flex items-center justify-center mb-4">
                    <IconComponent className="w-6 h-6 text-teal" />
                  </div>
                  <h3 className="font-bold text-navy text-lg mb-2">{service.title}</h3>
                  <p className="text-mid-grey text-sm mb-4">{service.description}</p>
                  <ul className="space-y-2">
                    {service.items.map((item, i) => (
                      <li key={i} className="flex items-start text-sm text-mid-grey">
                        <CheckCircle2 className="w-4 h-4 text-teal mr-2 flex-shrink-0 mt-0.5" />
                        {item}
                      </li>
                    ))}
                  </ul>
                </motion.div>
              );
            })}
          </motion.div>
        </div>
      </section>

      {/* Asset Classes */}
      <section className="py-16 bg-light-grey">
        <div className="max-w-7xl mx-auto px-6">
          <h3 className="font-playfair text-2xl text-navy mb-8 text-center">Asset Classes We Cover</h3>
          <div className="flex flex-wrap justify-center gap-4">
            {assetClasses.map((asset) => (
              <span key={asset} className="px-6 py-3 bg-white rounded-full text-navy font-medium text-sm hover:bg-teal hover:text-white transition-colors cursor-pointer shadow-sm">
                {asset}
              </span>
            ))}
          </div>
        </div>
      </section>

      {/* Why Choose Us */}
      <section className="py-20 md:py-28 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={staggerContainer} className="text-center mb-12">
            <motion.span variants={fadeInUp} className="text-teal font-medium text-sm uppercase tracking-wider">Our Advantage</motion.span>
            <motion.h2 variants={fadeInUp} className="font-playfair text-3xl md:text-4xl text-navy mt-4">Why H&G Capital Markets</motion.h2>
          </motion.div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {whyChooseUs.map((item, index) => (
              <motion.div
                key={item.title}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="bg-light-grey p-6 rounded-xl text-center"
              >
                <div className="w-12 h-12 bg-teal text-white rounded-full flex items-center justify-center mx-auto mb-4 font-bold">
                  {index + 1}
                </div>
                <h4 className="font-bold text-navy mb-2">{item.title}</h4>
                <p className="text-mid-grey text-sm">{item.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Transaction Types */}
      <section className="py-16 bg-navy">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <h3 className="text-white text-2xl font-bold mb-6">Transaction Expertise</h3>
          <p className="text-white/80 mb-8">
            From single-asset sales to complex portfolio transactions, we provide strategic guidance across the full spectrum of real estate capital markets activities.
          </p>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {['Acquisitions', 'Dispositions', 'Recapitalizations', 'Joint Ventures', 'Sale-Leasebacks', 'Refinancing', 'Development Finance', 'Portfolio Sales'].map((type) => (
              <div key={type} className="bg-white/10 px-4 py-3 rounded-lg">
                <span className="text-white text-sm">{type}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 md:py-28 bg-teal">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <h2 className="font-playfair text-4xl text-white mb-6">Ready to Transact?</h2>
          <p className="text-white/80 text-lg mb-8">Whether you're buying, selling, or financing commercial real estate, our capital markets experts are ready to help you achieve optimal outcomes.</p>
          <Link to={`/${locale}/contact`} className="btn-primary inline-flex items-center">
            Connect With Our Team <ArrowRight className="w-4 h-4 ml-2" />
          </Link>
        </div>
      </section>
    </div>
  );
};

export default CapitalMarketsPage;
